/**
 * Copilot action hooks for document state management
 *
 * These hooks dispatch document actions directly to the Zustand store via DocumentStore,
 * bypassing Redux. The DocumentStore handles:
 * 1. Injecting currentUser into actions (previously done by Redux currentUserMiddleware)
 * 2. Forwarding actions to the Zustand store's reduce() method
 *
 * The Zustand store's reduce() method notifies action listeners (ActionRecordService,
 * DocumentSaveService, PatchHandlerService) so middleware functionality is preserved.
 */

import {ApplyActionsResult, EnrichedCopilotActions} from "@buildwithflux/core";
import {IPartVersionData, UserUid} from "@buildwithflux/models";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";
import type {DocumentStore} from "modules/document/state/document";

export interface ApplyCopilotActionsArgs {
    actions: EnrichedCopilotActions;
    portalPart: IPartVersionData;
    groundPart: IPartVersionData;
    userUid: UserUid;
    resultCallback: (result: ApplyActionsResult) => void;
}

/**
 * Hook to get the applyCopilotActions action
 *
 * Dispatches directly to Zustand via DocumentStore, bypassing Redux.
 * This action applies copilot-generated actions to the document.
 *
 * @returns A function to apply copilot actions
 */
export function useApplyCopilotActions(): (args: ApplyCopilotActionsArgs) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (args: ApplyCopilotActionsArgs) => {
            documentStore.getState().applyCopilotActions(args);
        },
        [documentStore],
    );
}

/**
 * Factory function to create copilot actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch copilot actions.
 *
 * @param documentStore - The DocumentStore from FluxServices
 * @returns An object with copilot action methods
 */
export function createCopilotActions(documentStore: DocumentStore) {
    return {
        applyCopilotActions: (args: ApplyCopilotActionsArgs) => {
            documentStore.getState().applyCopilotActions(args);
        },
    };
}
